// ============================================================
// v8 — the card validator / trust gate (BOLT-010).
//
// The automated gate that REPLACES human code review: a card must
// pass it to be loaded or played. The sandbox (BOLT-009) already
// guarantees, by construction, that a card can only SHRINK Z
// (engine.clipZone), can't loop forever, has no I/O, and emits only
// opaque regions. So the validator's job is the residual judgement:
//
//  • runtime      — the card errors / times out on sampled inputs
//  • invalid      — it didn't return { op, region:id } (raw geometry)
//  • no-effect    — it never shrinks Z (useless)
//  • empties-zone — it collapses Z below a degenerate floor
//  • too-complex  — it builds more regions/ops than the bits-budget
//  • leaky        — the result's geometry TRACKS H beyond its reveal
//                   class (the centroid-recovery attack resolvePhoto-
//                   Penalty guards against, generalized)
//
// Returns a machine-readable verdict so the cost engine (BOLT-011)
// and the studio (BOLT-012) consume one source of truth.
// ============================================================
import { area, booleanPointInPolygon, centroid as turfCentroid, distance as turfDistance, point as turfPoint } from '@turf/turf';
import type { Feature, Polygon, MultiPolygon, Position } from 'geojson';
import { DEFAULT_BOUNDARY } from './packs/boundaries';
import { resolveBuiltin } from './resolveCard';
import { runCardScriptSync, isCardSandboxReady } from './sandbox/cardSandbox';
import { ft } from './constants';
import { mulberry32 } from './rng';
import type { CardDef, RevealClass } from './pack';
import type { CardFamily, LngLat, Zone } from './protocol';

type Poly = Feature<Polygon | MultiPolygon>;

export interface CardViolation {
  code: 'runtime' | 'invalid' | 'no-effect' | 'empties-zone' | 'too-complex' | 'leaky' | 'nondeterministic' | 'sandbox-unavailable';
  detail: string;
}
export interface ValidationVerdict {
  ok: boolean;
  revealClass: RevealClass;
  violations: CardViolation[];
  samplesRun: number;
  meanCutPct: number;
}

export interface ValidateOptions {
  boundary?: Zone;
  samples?: number;
  seed?: number;
}

// ---- budgets / thresholds (tunable; see spec §20) ----
const DEFAULT_SAMPLES = 24;
const FLOOR_M2 = 100; // a zone below this is degenerate (essentially "found")
const MAX_REGIONS = 64; // bits a card may encode via region constructors
const MAX_OPS = 16;
const NO_EFFECT_CUT = 0.5; // mean cut % below this ⇒ useless
const LEAK_DIST_M = 75; // result centroid this close to H ⇒ it localizes H…
const LEAK_AREA_FRAC = 0.5; // …and the zone is materially shrunk
const LEAK_SAMPLE_FRAC = 0.5; // …on this fraction of samples ⇒ leaky

const FAMILY_REVEAL: Record<CardFamily, RevealClass> = {
  radar: 'binary-disk',
  direction: 'wedge',
  thermometer: 'half-plane',
  photo: 'blob',
  // v24 BOLT-118: a landmark card reveals WHICH real feature is nearest, so
  // the seeker learns a named region — the same class as Quadrants/Borders.
  // Builtin-only, like the airhorn, so this is a formality for the validator.
  landmark: 'region-match',
  // v16 BOLT-072: the horn reveals by EAR, not geometry — community scripts
  // may not claim the family (builtin-only), so the class is a formality.
  airhorn: 'blob',
};

const pt = (p: LngLat) => turfPoint([p.lng, p.lat]);
const distM = (a: LngLat, b: LngLat) => turfDistance(pt(a), pt(b), { units: 'meters' });

/** Deterministically sample N points inside a polygon by bbox rejection. */
function sampleInside(boundary: Zone, n: number, rand: () => number): LngLat[] {
  const coords = (boundary.geometry.type === 'Polygon'
    ? boundary.geometry.coordinates
    : boundary.geometry.coordinates.flat()) as Position[][];
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  for (const ring of coords) for (const [lng, lat] of ring) {
    minLng = Math.min(minLng, lng); maxLng = Math.max(maxLng, lng);
    minLat = Math.min(minLat, lat); maxLat = Math.max(maxLat, lat);
  }
  const out: LngLat[] = [];
  let guard = 0;
  while (out.length < n && guard < n * 200) {
    guard++;
    const lng = minLng + rand() * (maxLng - minLng);
    const lat = minLat + rand() * (maxLat - minLat);
    if (booleanPointInPolygon([lng, lat], boundary as Feature<Polygon | MultiPolygon>)) out.push({ lat, lng });
  }
  return out;
}

function revealClassOf(def: CardDef): RevealClass {
  if (def.behavior.kind === 'script') return def.behavior.revealClass;
  if (def.behavior.ref === 'hider') return 'none';
  return def.family ? FAMILY_REVEAL[def.family] : 'binary-disk';
}

/** Validate a card. Built-ins are the trusted reference (always ok); scripts
 *  run the full battery in the sandbox over sampled inputs. */
export function validateCard(def: CardDef, opts: ValidateOptions = {}): ValidationVerdict {
  const revealClass = revealClassOf(def);
  const boundary = (opts.boundary ?? (DEFAULT_BOUNDARY.boundary as Zone)) as Poly;
  const Z0 = boundary as Zone;
  const z0Area = area(Z0);

  // Built-ins are authored by us — the trusted baseline.
  if (def.behavior.kind === 'builtin') {
    return { ok: true, revealClass, violations: [], samplesRun: 0, meanCutPct: 0 };
  }

  if (!isCardSandboxReady()) {
    return { ok: false, revealClass, violations: [{ code: 'sandbox-unavailable', detail: 'call loadCardSandbox() first' }], samplesRun: 0, meanCutPct: 0 };
  }

  const n = opts.samples ?? DEFAULT_SAMPLES;
  const rand = mulberry32((opts.seed ?? 12345) >>> 0 || 1);
  const S = (turfCentroid(Z0).geometry.coordinates as [number, number]); // a stable seeker near the middle
  const Sll: LngLat = { lat: S[1], lng: S[0] };
  const hs = sampleInside(Z0, n, rand);

  const violations: CardViolation[] = [];
  let errors = 0;
  let invalid = 0;
  let cutSum = 0;
  let minArea = Infinity;
  let maxRegions = 0;
  let maxOps = 0;
  let leakSamples = 0;
  let ran = 0;

  for (const H of hs) {
    const r = runCardScriptSync(def.behavior.source, { S: Sll, H, Z: Z0, boundary: Z0, seed: 1 });
    ran++;
    if (!r.ok) {
      if (/Raw geometry|opaque|must return/i.test(r.error ?? '')) invalid++;
      else errors++;
      continue;
    }
    maxRegions = Math.max(maxRegions, r.regionCount ?? 0);
    maxOps = Math.max(maxOps, r.ops?.length ?? 0);
    const a = area(r.zone!);
    minArea = Math.min(minArea, a);
    cutSum += r.cutPct ?? 0;
    // leakage: does the result's centroid track H while the zone is materially shrunk?
    const c = turfCentroid(r.zone as Feature<Polygon | MultiPolygon>).geometry.coordinates as [number, number];
    const dHC = distM({ lat: c[1], lng: c[0] }, H);
    if (dHC < LEAK_DIST_M && a < LEAK_AREA_FRAC * z0Area) leakSamples++;
  }

  const meanCutPct = ran > 0 ? cutSum / ran : 0;

  // determinism: identical ctx must give an identical zone. The sandbox removes
  // Date/Math.random, so this should always hold — re-run once as defense-in-depth.
  if (hs.length > 0) {
    const c1 = runCardScriptSync(def.behavior.source, { S: Sll, H: hs[0], Z: Z0, boundary: Z0, seed: 1 });
    const c2 = runCardScriptSync(def.behavior.source, { S: Sll, H: hs[0], Z: Z0, boundary: Z0, seed: 1 });
    if (c1.ok && c2.ok && JSON.stringify(c1.zone) !== JSON.stringify(c2.zone))
      violations.push({ code: 'nondeterministic', detail: 'identical ctx produced different zones' });
  }

  if (errors > ran * 0.25) violations.push({ code: 'runtime', detail: `${errors}/${ran} samples threw or timed out` });
  if (invalid > 0) violations.push({ code: 'invalid', detail: `${invalid}/${ran} samples returned a non-opaque / malformed result` });
  if (maxRegions > MAX_REGIONS || maxOps > MAX_OPS) violations.push({ code: 'too-complex', detail: `built ${maxRegions} regions / ${maxOps} ops (budget ${MAX_REGIONS}/${MAX_OPS})` });
  if (minArea < FLOOR_M2) violations.push({ code: 'empties-zone', detail: `collapsed Z to ${minArea.toFixed(1)} m² (floor ${FLOOR_M2})` });
  if (errors + invalid < ran && meanCutPct < NO_EFFECT_CUT) violations.push({ code: 'no-effect', detail: `mean cut ${meanCutPct.toFixed(2)}% — the card never shrinks the zone` });
  if (revealClass !== 'blob' && leakSamples >= ran * LEAK_SAMPLE_FRAC && ran > 0)
    violations.push({ code: 'leaky', detail: `result centroid pins H (<${LEAK_DIST_M} m) on ${leakSamples}/${ran} samples — encodes the hider's location beyond a '${revealClass}' reveal` });

  return { ok: violations.length === 0, revealClass, violations, samplesRun: ran, meanCutPct };
}

// ---------------- output hardening: grid-snap (generalizes resolvePhotoPenalty) ----------------
/** Snap every vertex of a zone to a coarse grid (cell = `cellM` metres), so a
 *  card cannot encode `H` into fine-grained vertex positions. Available to the
 *  runtime as defense-in-depth on top of the opaque-region firewall. */
export function snapZoneToGrid(zone: Zone, cellM: number): Zone {
  const refLat = (zone.geometry.type === 'Polygon' ? zone.geometry.coordinates[0][0][1] : zone.geometry.coordinates[0][0][0][1]) ?? 0;
  const cellLat = cellM / 111_320;
  const cellLng = cellM / (111_320 * Math.cos((refLat * Math.PI) / 180));
  const snap = ([lng, lat]: Position): Position => [Math.round(lng / cellLng) * cellLng, Math.round(lat / cellLat) * cellLat];
  const g = zone.geometry;
  const geometry =
    g.type === 'Polygon'
      ? { type: 'Polygon' as const, coordinates: g.coordinates.map((r) => r.map(snap)) }
      : { type: 'MultiPolygon' as const, coordinates: g.coordinates.map((poly) => poly.map((r) => r.map(snap))) };
  return { type: 'Feature', properties: zone.properties ?? {}, geometry } as Zone;
}

/** The grid cell (m) the validator/runtime snaps to. Mirrors the photo-penalty radius scale. */
export const SNAP_CELL_M = ft(20);
